import dayjs from "dayjs";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { DecimalHelper, StringHelper } from "@/helpers";
import { TransferRead, TransferCategoryI18nKeys } from "@/domains/transfer";
import { useAppDispatch, useAppSelector } from "@/stores";
import { tenantTransfersSelects, tenantTransfersThunks } from "./store";
// components
import { Popconfirm, Table } from "antd";
import { ColumnsType } from "antd/es/table";
import {
  CrownOutlined,
  ExportOutlined,
  ShoppingOutlined,
} from "@ant-design/icons";
import { CopyIconButton } from "@/components";
import { TableTopFilters } from "./TableTopFilters";

export const TenantTransfersListPage = () => {
  const { t } = useTranslation();

  const dispatch = useAppDispatch();

  const loading = useAppSelector(tenantTransfersSelects.loading);

  const queryParams = useAppSelector(tenantTransfersSelects.queryParams);

  const transfers = useAppSelector(tenantTransfersSelects.transfers);

  useEffect(() => {
    void dispatch(tenantTransfersThunks.list(queryParams));
  }, [dispatch, queryParams]);

  const columns: ColumnsType<TransferRead> = [
    {
      width: 100,
      className: "v:top",
      title: t("domains.Transfer.id"),
      dataIndex: "id",
      render(_, { id }) {
        return (
          <div className="word-break:break-all">
            <span className="mr:4">{id}</span>
            <CopyIconButton text={id} />
          </div>
        );
      },
    },
    {
      width: 260,
      className: "v:top",
      title: `${t("domains.Transfer.belongName")} | ${t("domains.Transfer.orderId")}`,
      key: "belong_order",
      render(_, { belong, order }) {
        return (
          <>
            <div className="word-break:break-all">
              <CrownOutlined className="mr:8" />
              {belong?.name && StringHelper.getLength(belong?.name) >= 25 ? (
                <>
                  <span className="inline-block w:175 mr:8">
                    {StringHelper.cutString(belong?.name, 20) + "..."}
                  </span>
                  <Popconfirm
                    title={t("domains.Transfer.belongName")}
                    description={belong?.name}
                    showCancel={false}
                    okText={t("systems.Action.confirm")}
                    className="mr:8"
                  >
                    <ExportOutlined />
                  </Popconfirm>
                  <CopyIconButton text={belong?.name} />
                </>
              ) : (
                <>
                  <span className="inline-block word-break:break-all w:197 mr:8">
                    {belong?.name ?? "?"}
                  </span>
                  <CopyIconButton text={belong?.name} />
                </>
              )}
            </div>
            <div className="word-break:break-all">
              <ShoppingOutlined className="mr:8" />
              {order?.id && order?.id?.length >= 25 ? (
                <>
                  <span className="inline-block w:175 mr:8">
                    {StringHelper.ellipsisMiddle(order?.id, 25)}
                  </span>
                  <Popconfirm
                    title={t("domains.Transfer.orderId")}
                    description={
                      <>
                        <span className="inline-block word-break:break-all w:200 mr:8 mb:8">
                          {order?.id}
                        </span>
                        <CopyIconButton text={order?.id} />
                      </>
                    }
                    showCancel={false}
                    okText={t("systems.Action.confirm")}
                    className="mr:8"
                  >
                    <ExportOutlined />
                  </Popconfirm>
                  <CopyIconButton text={order?.id} />
                </>
              ) : (
                <>
                  <span className="inline-block w:197 mr:8">
                    {order?.id ?? "?"}
                  </span>
                  <CopyIconButton text={order?.id} />
                </>
              )}
            </div>
          </>
        );
      },
    },
    {
      width: 90,
      className: "v:top",
      title: t("domains.Transfer.category"),
      render(_, { category }) {
        const i18nKey =
          TransferCategoryI18nKeys[
            category as keyof typeof TransferCategoryI18nKeys
          ];
        return <div>{i18nKey ? t(i18nKey) : category}</div>;
      },
    },
    {
      width: 90,
      align: "right",
      className: "v:top",
      title: t("domains.Transfer.amount"),
      sorter: () => 1,
      render(_, { amount }) {
        return <div className="t:right">{DecimalHelper.fixed(amount, 2)}</div>;
      },
    },
    {
      width: 90,
      align: "right",
      className: "v:top",
      title: t("domains.Transfer.userBalance0"),
      render(_, { user_balance0 }) {
        return (
          <div className="t:right">
            {user_balance0 ? DecimalHelper.fixed(user_balance0, 2) : "-"}
          </div>
        );
      },
    },
    {
      width: 90,
      align: "right",
      className: "v:top",
      title: t("domains.Transfer.userBalance1"),
      render(_, { user_balance1 }) {
        return (
          <div className="t:right">
            {user_balance1 ? DecimalHelper.fixed(user_balance1, 2) : "-"}
          </div>
        );
      },
    },
    {
      className: "v:top",
      title: t("domains.Transfer.note"),
      render(_, { note }) {
        return <div>{note}</div>;
      },
    },
    {
      width: 90,
      align: "right",
      className: "v:top",
      title: t("domains.Transfer.recogedAt"),
      sorter: () => 1,
      render(_, { recoged_at }) {
        return recoged_at ? <div>{dayjs.$ldts(recoged_at)}</div> : null;
      },
    },
  ];

  return (
    <div className="flex flex:col">
      <TableTopFilters />

      <Table
        rowKey="id"
        tableLayout="fixed"
        loading={loading}
        columns={columns}
        dataSource={transfers}
        scroll={{ x: "1200px" }}
      />
    </div>
  );
};
